import { createSlice } from '@reduxjs/toolkit';
import { FeedManagementState } from './types';

export const initialState: FeedManagementState = {
  loading: false,
  socialFeedLoading: false,
  pageLoading: false,
  publishing: false,
  error: undefined,
  channels: [],
  pages: [],
  feeds: [],
  socialFeeds: [],
  currentSocialFeedConfiguration: {},
  unsavedFilterChanges: undefined
};

const feedManagementSlice = createSlice({
  name: 'feedManagement',
  initialState,
  reducers: {
    storeUnsavedFilterChanges: (state, action) => {
      const { socialFeedId, filters, isMobile } = action.payload;
      if (!state.unsavedFilterChanges || state.unsavedFilterChanges.socialFeedId !== socialFeedId) {
        state.unsavedFilterChanges = { socialFeedId };
      }

      if (isMobile) {
        state.unsavedFilterChanges.mobileFilters = filters;
      } else {
        state.unsavedFilterChanges.webFilters = filters;
      }
    },

    clearUnsavedFilterChanges: state => {
      state.unsavedFilterChanges = undefined;
    },
    updateSocialFeedSettingsLocal: (state, action) => {
      const { socialFeedId, settings, isGlobal, isMobile } = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => {
        if (feed.id === socialFeedId) {
          const updatedFeed = { ...feed };
          if (isGlobal) {
            if (isMobile) {
              updatedFeed.mobileGlobal = { ...updatedFeed.mobileGlobal, ...settings };
            } else {
              updatedFeed.global = { ...updatedFeed.global, ...settings };
            }
          } else {
            if (isMobile) {
              updatedFeed.mobileFilter = { ...updatedFeed.mobileFilter, ...settings };
            } else {
              updatedFeed.filter = { ...updatedFeed.filter, ...settings };
            }
          }
          return updatedFeed;
        }
        return feed;
      });
    },
    resetState: () => {
      return initialState;
    },
    fetchChannelsByMarketer: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    fetchChannelsByMarketerSuccess: (state, action) => {
      state.loading = false;
      state.channels = action.payload;
    },
    fetchChannelsByMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchPagesByMarketer: state => {
      state.pageLoading = true;
      state.error = undefined;
    },
    fetchPagesByMarketerSuccess: (state, action) => {
      state.pageLoading = false;
      state.pages = action.payload;
    },
    fetchPagesByMarketerError: (state, action) => {
      state.pageLoading = false;
      state.error = action.payload;
    },
    fetchFeedsByMarketer: state => {
      state.loading = true;
      state.error = undefined;
    },
    fetchFeedsByMarketerSuccess: (state, action) => {
      state.loading = false;
      state.feeds = action.payload;
    },
    fetchFeedsByMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchSocialFeedsByMarketer: state => {
      state.socialFeedLoading = true;
      state.error = undefined;
    },
    fetchSocialFeedsByMarketerSuccess: (state, action) => {
      state.socialFeedLoading = false;
      state.socialFeeds = action.payload;
    },
    fetchSocialFeedsByMarketerError: (state, action) => {
      state.socialFeedLoading = false;
      state.error = action.payload;
    },
    createPageForMarketer: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    createPageForMarketerSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.pages = [...state.pages, action.payload];
    },
    createPageForMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    createFeedForMarketer: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    createFeedForMarketerSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.feeds = [...state.feeds, action.payload];
    },
    createFeedForMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateFeedForMarketer: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateFeedForMarketerSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.feeds = [...state.feeds.filter(feed => feed.id !== action.payload.id), action.payload];
    },
    updateFeedForMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    createSocialFeedForMarketer: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    createSocialFeedForMarketerSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.socialFeeds = [...state.socialFeeds, action.payload];
    },
    createSocialFeedForMarketerError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateGlobalSocialFeedSettings: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateGlobalSocialFeedSettingsSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    updateGlobalSocialFeedSettingsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateGlobalMobileSocialFeedSettings: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateGlobalMobileSocialFeedSettingsSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    updateGlobalMobileSocialFeedSettingsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedFilterSettings: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateSocialFeedFilterSettingsSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    updateSocialFeedFilterSettingsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedMobileFilterSettings: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateSocialFeedMobileFilterSettingsSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    updateSocialFeedMobileFilterSettingsError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateFeedProperties: (state: any, action: any) => {
      state.loading = true;
      state.error = undefined;
    },
    updateFeedPropertiesSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;

      const feedIndex = state.feeds.findIndex((feed: any) => feed.id === action.payload.id);
      if (feedIndex !== -1) {
        state.feeds[feedIndex] = {
          ...state.feeds[feedIndex],
          settings: action.payload.properties
        };
      }
    },
    updateFeedPropertiesError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedTitle: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedTitleSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedTitleError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedDescription: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedDescriptionSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedDescriptionError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedPrefix: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedPrefixSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedPrefixError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedDomain: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedDomainSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedDomainError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedLogo: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedLogoSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedLogoError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateSocialFeedFavicon: (state, action) => {
      state.loading = true;
      state.error = '';
    },
    updateSocialFeedFaviconSuccess: state => {
      state.loading = false;
      state.error = '';
    },
    updateSocialFeedFaviconError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updatePage: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updatePageSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedPage = action.payload;
      state.pages = state.pages.map(page => (page.id === updatedPage.id ? updatedPage : page));
    },
    updatePageError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateCustomizeFeed: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateCustomizeFeedSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedFeed = action.payload;
      state.feeds = state.feeds.map(feed => (feed.id === updatedFeed.id ? updatedFeed : feed));
    },
    updateCustomizeFeedError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    publishSocialFeed: (state, action) => {
      state.publishing = true;
      state.error = undefined;
    },

    publishSocialFeedUpdate: (state, action) => {
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    publishSocialFeedSuccess: (state, action) => {
      state.publishing = false;
      state.error = undefined;
      // const updatedSocialFeed = action.payload;
      // state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    publishSocialFeedError: (state, action) => {
      state.publishing = false;
      state.error = action.payload;
    },
    updateSocialFeedContent: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    updateSocialFeedContentSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      const updatedSocialFeed = action.payload;
      state.socialFeeds = state.socialFeeds.map(feed => (feed.id === updatedSocialFeed.id ? updatedSocialFeed : feed));
    },
    updateSocialFeedContentError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchSocialFeedConfiguration: (state, action) => {
      state.loading = true;
      state.error = undefined;
    },
    fetchSocialFeedConfigurationSuccess: (state, action) => {
      state.loading = false;
      state.error = undefined;
      state.currentSocialFeedConfiguration = { [action.payload.id]: action.payload.data };
    },
    fetchSocialFeedConfigurationError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // updateSocialFeedConfiguration: (state, action) => {
    //   state.currentSocialFeedConfiguration = {
    //     [action.payload.id]: {
    //       ...state.currentSocialFeedConfiguration[action.payload.id],
    //       [action.payload.key]: action.payload.value
    //     }
    //   };
    // }
    updateSocialFeedBannerConfiguration: (state, action) => {
      state.currentSocialFeedConfiguration = {
        [action.payload.id]: {
          ...state.currentSocialFeedConfiguration[action.payload.id],
          [action.payload.device]: {
            ...state.currentSocialFeedConfiguration[action.payload.id][action.payload.device],
            banner: action.payload.value
          }
        }
      };
    },
    updateSocialFeedPlayerConfiguration: (state, action) => {
      state.currentSocialFeedConfiguration = {
        [action.payload.id]: {
          ...state.currentSocialFeedConfiguration[action.payload.id],
          [action.payload.device]: {
            ...state.currentSocialFeedConfiguration[action.payload.id][action.payload.device],
            feed: {
              ...state.currentSocialFeedConfiguration[action.payload.id][action.payload.device]['feed'],
              [action.payload.key]: action.payload.value
            }
          }
        }
      };
    },
    updateSocialFeedNavigationConfiguration: (state, action) => {
      state.currentSocialFeedConfiguration = {
        [action.payload.id]: {
          ...state.currentSocialFeedConfiguration[action.payload.id],
          [action.payload.device]: {
            ...state.currentSocialFeedConfiguration[action.payload.id][action.payload.device],
            navigation: {
              ...state.currentSocialFeedConfiguration[action.payload.id][action.payload.device]['navigation'],
              [action.payload.key]: action.payload.value
            }
          }
        }
      };
    }
  }
});

export const {
  resetState,
  storeUnsavedFilterChanges,
  clearUnsavedFilterChanges,
  updateSocialFeedSettingsLocal,
  fetchChannelsByMarketer,
  fetchChannelsByMarketerSuccess,
  fetchChannelsByMarketerError,
  fetchPagesByMarketer,
  fetchPagesByMarketerSuccess,
  fetchPagesByMarketerError,
  fetchFeedsByMarketer,
  fetchFeedsByMarketerSuccess,
  fetchFeedsByMarketerError,
  createPageForMarketer,
  createPageForMarketerSuccess,
  createPageForMarketerError,
  createFeedForMarketer,
  createFeedForMarketerSuccess,
  createFeedForMarketerError,
  createSocialFeedForMarketer,
  createSocialFeedForMarketerSuccess,
  createSocialFeedForMarketerError,
  updateGlobalSocialFeedSettings,
  updateGlobalSocialFeedSettingsSuccess,
  updateGlobalSocialFeedSettingsError,
  updateGlobalMobileSocialFeedSettings,
  updateGlobalMobileSocialFeedSettingsSuccess,
  updateGlobalMobileSocialFeedSettingsError,
  updateSocialFeedFilterSettings,
  updateSocialFeedFilterSettingsSuccess,
  updateSocialFeedFilterSettingsError,
  updateSocialFeedMobileFilterSettings,
  updateSocialFeedMobileFilterSettingsSuccess,
  updateSocialFeedMobileFilterSettingsError,
  updateFeedForMarketer,
  updateFeedForMarketerSuccess,
  updateFeedForMarketerError,
  updateFeedProperties,
  updateFeedPropertiesSuccess,
  updateFeedPropertiesError,
  fetchSocialFeedsByMarketer,
  fetchSocialFeedsByMarketerSuccess,
  fetchSocialFeedsByMarketerError,
  updateSocialFeedTitle,
  updateSocialFeedTitleSuccess,
  updateSocialFeedTitleError,
  updateSocialFeedDescription,
  updateSocialFeedDescriptionSuccess,
  updateSocialFeedDescriptionError,
  updateSocialFeedPrefix,
  updateSocialFeedPrefixSuccess,
  updateSocialFeedPrefixError,
  updateSocialFeedDomain,
  updateSocialFeedDomainSuccess,
  updateSocialFeedDomainError,
  updateSocialFeedLogo,
  updateSocialFeedLogoSuccess,
  updateSocialFeedLogoError,
  updateSocialFeedFavicon,
  updateSocialFeedFaviconSuccess,
  updateSocialFeedFaviconError,
  updatePage,
  updatePageSuccess,
  updatePageError,
  updateCustomizeFeed,
  updateCustomizeFeedSuccess,
  updateCustomizeFeedError,
  publishSocialFeed,
  publishSocialFeedSuccess,
  publishSocialFeedError,
  publishSocialFeedUpdate,
  updateSocialFeedContent,
  updateSocialFeedContentSuccess,
  updateSocialFeedContentError,
  fetchSocialFeedConfiguration,
  fetchSocialFeedConfigurationSuccess,
  fetchSocialFeedConfigurationError,
  updateSocialFeedBannerConfiguration,
  updateSocialFeedPlayerConfiguration,
  updateSocialFeedNavigationConfiguration
} = feedManagementSlice.actions;

export default feedManagementSlice.reducer;
